import {
  AutoComplete,
  FormContainer,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IProfileDashboardPanel } from "../../../../../../models/profile-dashboard-panel";
import {
  createRoleDashboardPanels,
  listDashboardPanels,
  updateRoleDashboardPanels,
} from "../../../../../../services/profileDashboardPanels.service";
import {
  profileDashboardPanelSchema,
  TProfileDashboardPanelForm,
} from "./profileDashboardPanelSchema";

const translationPath = "page.register.roles.";

interface IProfileDashboardPanelProps {
  id?: string;
  roleId?: string;
  rows: IProfileDashboardPanel[];
  onBack: () => void;
}

export const ProfileDashboardPanel = memo(
  ({ id, roleId, rows, onBack }: IProfileDashboardPanelProps) => {
    const { t } = useTranslation();

    const { sendRequest: create, loading: cLoading } = useFetch(
      createRoleDashboardPanels,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateRoleDashboardPanels,
    );

    const row = useMemo(
      () => rows.find((currentRow) => currentRow.id === id) ?? null,
      [rows, id],
    );

    const getHandler = useCallback(
      async (): Promise<Treturn<TProfileDashboardPanelForm>> => {
        const formData: TProfileDashboardPanelForm = {
          dashboardPanel: row
            ? {
                id: row.dashboardPanel.id,
                name: row.dashboardPanel.name,
              }
            : null,
        };

        return {
          data: formData,
          success: true,
        };
      },
      [row],
    );

    const updateDashboardPanelIds = useCallback(
      (dashboardPanelId: string) => {
        return Array.from(
          new Set(
            rows.map((currentRow) =>
              currentRow.id === row?.id
                ? dashboardPanelId
                : currentRow.dashboardPanelId
            )
          )
        );
      },
      [row?.id, rows],
    );

    const saveHandler = (v: TProfileDashboardPanelForm) =>
      create({
        roleId: roleId ?? "",
        dashboardPanelIds: v.dashboardPanel ? [v.dashboardPanel.id] : [],
      });

    const updateHandler = (v: TProfileDashboardPanelForm, id: string) =>
      update({
        id: roleId ?? "",
        item: {
          roleId: roleId ?? "",
          dashboardPanelIds: v.dashboardPanel
            ? updateDashboardPanelIds(v.dashboardPanel.id)
            : rows.map((currentRow) => currentRow.dashboardPanelId),
        },
      });

    const loading = cLoading || uLoading;

    return (
      <FormContainer<TProfileDashboardPanelForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "dashboard")}
        schema={profileDashboardPanelSchema}
        isModalForm={true}
        subtitle={
          id
            ? t(translationPath + "edit_link")
            : t(translationPath + "add_dashboard")
        }
        backButtonOnClick={onBack}
        redirectOnSave={false}
        onSuccess={onBack}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AutoComplete
                request={listDashboardPanels}
                name="dashboardPanel"
                size="small"
                getOptionLabel={({ name }) => name}
                label={t(translationPath + "dashboard")}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
